import { useEffect } from 'react';
import { t } from 'i18next';

import { useAllTradingPhotoCardsQuery } from '../hooks/queries/useTradingPhotoCardsQuery';
import { useTradingPhotoCardModal } from './hooks/useTradingPhotoCardsModal';
import MyTradingPhotoCardsHeader from './components/MyTradingPhotoCardsHeader';
import TradingCategoryTab from './components/TradingCategoryTab';
import TradingPhotoCardsList from './components/TradingPhotoCardsList';
import PopUpBasic from '../components/popup/PopUpBasic';
import TradingPhotoCardActionsBar from './components/TradingPhotoCardActionsBar';
import ExcludePhotoCardSection from './components/ExcludePhotoCardSection';
import TradingPhotoCardsListSkeleton from './components/TradingPhotoCardSkeleton';
import { useSelectedTradingCardsStore } from './store/useSelectedTradingCardsStore';
import { useMessageHandler } from './hooks/useMessageHandler';
import { useTabFromQueryParam } from '../hooks/useTabFromQueryParam';

const MyTradingPhotoCardsPage = () => {
  const { activeTab, goToTab } = useTabFromQueryParam();
  const { isOpen, openModal, closeModal, handleConfirm } = useTradingPhotoCardModal();
  const { selectedPhotoCards, clearPhotoCards, setSelectedPhotoCards } =
    useSelectedTradingCardsStore();

  const { tradingPhotoCards, isLoading } = useAllTradingPhotoCardsQuery({
    category: activeTab === 0 ? 'sale' : 'exchange'
  });

  const handleTabChange = (tab: number) => {
    if (selectedPhotoCards.length > 0) {
      openModal('tabChange', tab);
    } else {
      goToTab(tab);
    }
  };

  const handleTabChangeClear = (tabIndex: number) => {
    clearPhotoCards();
    goToTab(tabIndex);
  };

  const handleToggleSelectAll = () => {
    const isAllSelected = selectedPhotoCards.length === tradingPhotoCards.length;
    if (isAllSelected) {
      clearPhotoCards();
    } else {
      setSelectedPhotoCards(tradingPhotoCards);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: 페이지 진입 시 모든 카드 선택 해제
  useEffect(() => {
    clearPhotoCards();
  }, []);

  // 메시지를 받아서 포토카드 선택 초기화 및 완료 표시
  useMessageHandler();

  return (
    <div className="min-h-screen bg-pure-white dark:bg-background-dark pt-[calc(env(safe-area-inset-top))] pb-[calc(env(safe-area-inset-bottom))]">
      <MyTradingPhotoCardsHeader setIsPopUpOpen={() => openModal('historyBack')} />
      <div className="px-[24px] mt-[58px]">
        <TradingCategoryTab activeTab={activeTab} onTabChange={handleTabChange} />
      </div>
      {activeTab === 1 && <ExcludePhotoCardSection />}
      <div className="px-[24px] mt-[20px] pb-[200px]">
        {isLoading ? (
          <TradingPhotoCardsListSkeleton showPrice={activeTab === 0} />
        ) : (
          <TradingPhotoCardsList activeTab={activeTab} tradingPhotoCards={tradingPhotoCards} />
        )}
      </div>
      <TradingPhotoCardActionsBar
        activeTab={activeTab}
        totalCount={tradingPhotoCards.length}
        onToggleSelectAll={handleToggleSelectAll}
      />
      {isOpen && (
        <div className="fixed top-0 left-0 h-screen w-full z-10 bg-[rgba(0,0,0,0.5)] dark:bg-[rgba(0,0,0,0.65)]">
          <PopUpBasic
            type="large"
            title={t('NewTradingPhotoCards.popUpTitle')}
            description={t('NewTradingPhotoCards.popUpDescription')}
            primaryButtonText={t('NewTradingPhotoCards.popUpPrimaryButtonText')}
            secondaryButtonText={t('NewTradingPhotoCards.popUpSecondaryButtonText')}
            onPrimaryButtonClick={closeModal}
            onSecondaryButtonClick={() => handleConfirm({ onTabChangeClear: handleTabChangeClear })}
            className="w-[311px] h-[246px] fixed bottom-[250px] left-1/2 transform -translate-x-1/2 z-20"
          />
        </div>
      )}
    </div>
  );
};

export default MyTradingPhotoCardsPage;
